import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  createAttendance,
  deleteAttendance,
} from "features/EmployeeAttendance/services/EmployeeAttendanceService";
import {
  type AddAttendanceRequest,
  type AttendanceDetails,
  type SessionInput,
} from "features/EmployeeAttendance/types/employee-attendance.types";
import moment from "moment";
import schema from "./schema";
import DeleteModal from "components/common/DeleteModal";
import {
  EmployeeAttendanceLocationType,
  EmployeeAttendanceLocationTypeLabelMap,
  EmployeeAttendanceStatus,
} from "features/EmployeeAttendance/constants/employee-attendance.constants";

function normalizeTime(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("HH:mm") : "";
}

function defaultInTime(): string {
  return "09:00";
}

function computeDuration(inTime: string, outTime: string): string | null {
  const inM = moment(inTime, "HH:mm", true);
  const outM = moment(outTime, "HH:mm", true);
  if (!inM.isValid() || !outM.isValid() || outM.isSameOrBefore(inM)) return null;
  const mins = outM.diff(inM, "minutes");
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function toMinutes(time: string): number | null {
  const m = moment(time, "HH:mm", true);
  return m.isValid() ? m.hours() * 60 + m.minutes() : null;
}

/** Only complete (in+out both set, out after in) sessions are checked — a session still being entered isn't flagged. */
function toRange(
  inTime: string,
  outTime: string,
): { inM: number; outM: number } | null {
  const inM = toMinutes(inTime);
  const outM = toMinutes(outTime);
  if (inM == null || outM == null || outM <= inM) return null;
  return { inM, outM };
}

function rangesOverlap(
  a: { inM: number; outM: number },
  b: { inM: number; outM: number },
): boolean {
  return a.inM < b.outM && b.inM < a.outM;
}

type SessionRow = {
  id?: number;
  inTime: string;
  outTime: string;
  inLocationType: EmployeeAttendanceLocationType | null;
  outLocationType: EmployeeAttendanceLocationType | null;
};

function sessionFromRecord(s: { id?: number; inTime?: string | null; outTime?: string | null; inLocationType?: EmployeeAttendanceLocationType | null; outLocationType?: EmployeeAttendanceLocationType | null }): SessionRow {
  return {
    id: s.id,
    inTime: normalizeTime(s.inTime),
    outTime: normalizeTime(s.outTime),
    inLocationType: s.inLocationType ?? null,
    outLocationType: s.outLocationType ?? null,
  };
}

const LocationTypeSelect = ({
  value,
  onChange,
  id,
}: {
  value: EmployeeAttendanceLocationType | null;
  onChange: (v: EmployeeAttendanceLocationType | null) => void;
  id?: string;
}) => (
  <Input
    id={id}
    type="select"
    bsSize="sm"
    value={value ?? ""}
    onChange={(e) => onChange((e.target.value as EmployeeAttendanceLocationType) || null)}
  >
    <option value="">— Not specified —</option>
    {Object.values(EmployeeAttendanceLocationType).map((v) => (
      <option key={v} value={v}>
        {EmployeeAttendanceLocationTypeLabelMap[v]}
      </option>
    ))}
  </Input>
);

const AttendanceForm = ({
  show,
  onClose,
  onSuccess,
  selectedDate,
  employeeId,
  joinDate,
  data,
}: {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedDate: string;
  employeeId: number;
  joinDate: string;
  data: AttendanceDetails | null;
}) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [sessions, setSessions] = useState<SessionRow[]>([]);

  const minAllowedDate = joinDate ? moment(joinDate).format("YYYY-MM-DD") : "";
  const selectedOrMinDate =
    minAllowedDate && moment(selectedDate).isBefore(minAllowedDate, "day")
      ? minAllowedDate
      : selectedDate;

  // Sync sessions from data whenever the modal opens with a record
  useEffect(() => {
    if (show) {
      setSessions(
        Array.isArray(data?.sessions) && data.sessions.length > 0
          ? data.sessions.map(sessionFromRecord)
          : [],
      );
    }
  }, [show, data]);

  const initialValues = useMemo(
    () => ({
      employeeId,
      date: selectedOrMinDate,
      isHalfDay: data?.isHalfDay ?? false,
      remarks: data?.remarks ?? "",
      inTime: normalizeTime(data?.inTime) || defaultInTime(),
      outTime: normalizeTime(data?.outTime),
      inLocationType: data?.inLocationType ?? null,
      outLocationType: data?.outLocationType ?? null,
    }),
    [data, employeeId, selectedOrMinDate],
  );

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        if (hasOverlap) {
          toast.error("Sessions cannot overlap — please fix the highlighted times.");
          return;
        }
        const attendanceDate = String(values.date ?? selectedOrMinDate).slice(0, 10);
        if (minAllowedDate && moment(attendanceDate).isBefore(minAllowedDate, "day")) {
          toast.error("Attendance cannot be marked before employee join date");
          return;
        }
        const inDateTime = values.inTime
          ? moment(`${attendanceDate} ${values.inTime}`, "YYYY-MM-DD HH:mm", true)
          : null;
        const outDateTime = values.outTime
          ? moment(`${attendanceDate} ${values.outTime}`, "YYYY-MM-DD HH:mm", true)
          : null;

        const sessionsPayload: SessionInput[] = sessions
          .filter((s) => Boolean(s.inTime))
          .map((s) => {
            const sIn = moment(`${attendanceDate} ${s.inTime}`, "YYYY-MM-DD HH:mm", true);
            const sOut = s.outTime
              ? moment(`${attendanceDate} ${s.outTime}`, "YYYY-MM-DD HH:mm", true)
              : null;
            return {
              ...(s.id != null ? { id: s.id } : {}),
              inTime: sIn.isValid() ? sIn.toISOString() : s.inTime,
              outTime: sOut?.isValid() ? sOut.toISOString() : undefined,
              inLocationType: s.inLocationType ?? undefined,
              outLocationType: s.outLocationType ?? undefined,
            };
          });

        const payload: AddAttendanceRequest = {
          employeeId: Number(values.employeeId),
          date: attendanceDate,
          status: EmployeeAttendanceStatus.PRESENT,
          isHalfDay: values.isHalfDay || undefined,
          remarks: values.remarks?.trim() || null,
          inTime: inDateTime?.isValid() ? inDateTime.toISOString() : null,
          outTime: outDateTime?.isValid() ? outDateTime.toISOString() : null,
          inLocationType: values.inLocationType ?? null,
          outLocationType: values.outLocationType ?? null,
          sessions: sessionsPayload,
        };
        const response = await createAttendance(payload);
        if (response.success) {
          toast.success(response.message);
          handleOnClose();
          onSuccess();
        }
      } catch (err) {
        toast.error(
          (err as any)?.response?.data?.message ?? "Could not save attendance.",
        );
      }
    },
  });

  function handleOnClose() {
    onClose();
    validation.resetForm();
    setSessions([]);
  }

  const handleDelete = async () => {
    if (!data) return;
    try {
      const response = await deleteAttendance(data.id);
      if (response.success) toast.success(response.message);
      setDeleteModal(false);
      handleOnClose();
      onSuccess();
    } catch (err) {
      toast.error(
        (err as any)?.response?.data?.message ??
          (err as any)?.message ??
          "Could not delete attendance.",
      );
    }
  };

  const addSession = () => {
    setSessions((prev) => [
      ...prev,
      { inTime: "", outTime: "", inLocationType: null, outLocationType: null },
    ]);
  };

  const removeSession = (idx: number) => {
    setSessions((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateSession = (idx: number, patch: Partial<SessionRow>) => {
    setSessions((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    );
  };

  const primaryDuration = computeDuration(
    validation.values.inTime,
    validation.values.outTime,
  );

  /** Index 0 is the primary session; the rest mirror `sessions`. */
  const overlapFlags = useMemo(() => {
    const ranges = [
      toRange(validation.values.inTime, validation.values.outTime),
      ...sessions.map((s) => toRange(s.inTime, s.outTime)),
    ];
    return ranges.map((r, i) => {
      if (!r) return false;
      return ranges.some(
        (other, j) => j !== i && other != null && rangesOverlap(r, other),
      );
    });
  }, [validation.values.inTime, validation.values.outTime, sessions]);

  const hasOverlap = overlapFlags.some(Boolean);

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        zIndex={1200}
      />

      <Modal isOpen={show} toggle={handleOnClose} centered size="lg" zIndex={1100}>
        <ModalHeader toggle={handleOnClose}>
          <span>
            {data ? "Edit Attendance" : "Mark Attendance"}
            <span className="text-muted fw-normal ms-2 small">
              {moment(selectedOrMinDate).format("dddd, DD MMM YYYY")}
            </span>
          </span>
        </ModalHeader>

        <ModalBody>
          <Form
            id="attendance-form"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            <Row className="g-3">
              {/* Half-day toggle — only relevant when marking new attendance */}
              {!data ? (
                <Col xs={12}>
                  <div className="form-check form-switch mb-0">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="att-is-half-day"
                      checked={validation.values.isHalfDay}
                      onChange={(e) =>
                        validation.setFieldValue("isHalfDay", e.target.checked)
                      }
                    />
                    <Label className="form-check-label mb-0" htmlFor="att-is-half-day">
                      Half day
                    </Label>
                  </div>
                </Col>
              ) : null}

              {/* Primary session */}
              <Col xs={12}>
                <div className="border rounded-2 p-3">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: "0.7rem" }}>
                      Session #1
                    </span>
                    {primaryDuration ? (
                      <span className="badge bg-success-subtle text-success fw-normal">
                        {primaryDuration}
                      </span>
                    ) : null}
                    {overlapFlags[0] ? (
                      <span className="badge bg-danger-subtle text-danger fw-normal">
                        Overlaps another session
                      </span>
                    ) : null}
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <Label className="form-label small">In time</Label>
                      <Input
                        type="time"
                        name="inTime"
                        value={validation.values.inTime || ""}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(validation.touched.inTime && validation.errors.inTime) ||
                          overlapFlags[0]
                        }
                      />
                      {validation.touched.inTime && validation.errors.inTime ? (
                        <FormFeedback type="invalid" className="d-block">
                          {validation.errors.inTime}
                        </FormFeedback>
                      ) : null}
                    </Col>

                    <Col md={6}>
                      <Label className="form-label small">Out time</Label>
                      <Input
                        type="time"
                        name="outTime"
                        value={validation.values.outTime || ""}
                        min={validation.values.inTime || undefined}
                        disabled={!validation.values.inTime}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          !!(validation.touched.outTime && validation.errors.outTime) ||
                          overlapFlags[0]
                        }
                      />
                      {validation.touched.outTime && validation.errors.outTime ? (
                        <FormFeedback type="invalid" className="d-block">
                          {validation.errors.outTime}
                        </FormFeedback>
                      ) : overlapFlags[0] ? (
                        <FormFeedback type="invalid" className="d-block">
                          This time range overlaps another session.
                        </FormFeedback>
                      ) : null}
                    </Col>

                    <Col md={6}>
                      <Label className="form-label small">In location type</Label>
                      <LocationTypeSelect
                        value={validation.values.inLocationType}
                        onChange={(v) => validation.setFieldValue("inLocationType", v)}
                      />
                    </Col>

                    <Col md={6}>
                      <Label className="form-label small">Out location type</Label>
                      <LocationTypeSelect
                        value={validation.values.outLocationType}
                        onChange={(v) => validation.setFieldValue("outLocationType", v)}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* Additional sessions */}
              {sessions.map((s, idx) => {
                const dur = computeDuration(s.inTime, s.outTime);
                const overlaps = overlapFlags[idx + 1];
                return (
                  <Col xs={12} key={idx}>
                    <div className="border rounded-2 p-3">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="badge bg-light text-muted border"
                            style={{ fontSize: "0.7rem" }}
                          >
                            Session #{idx + 2}
                          </span>
                          {dur ? (
                            <span className="badge bg-success-subtle text-success fw-normal">
                              {dur}
                            </span>
                          ) : null}
                          {overlaps ? (
                            <span className="badge bg-danger-subtle text-danger fw-normal">
                              Overlaps another session
                            </span>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost-danger p-1 lh-1"
                          title="Remove session"
                          onClick={() => removeSession(idx)}
                        >
                          <i className="ri-close-line" />
                        </button>
                      </div>

                      <Row className="g-3">
                        <Col md={6}>
                          <Label className="form-label small">In time</Label>
                          <Input
                            type="time"
                            bsSize="sm"
                            value={s.inTime}
                            onChange={(e) => updateSession(idx, { inTime: e.target.value })}
                            invalid={overlaps}
                          />
                        </Col>

                        <Col md={6}>
                          <Label className="form-label small">Out time</Label>
                          <Input
                            type="time"
                            bsSize="sm"
                            value={s.outTime}
                            min={s.inTime || undefined}
                            disabled={!s.inTime}
                            onChange={(e) => updateSession(idx, { outTime: e.target.value })}
                            invalid={overlaps}
                          />
                          {overlaps ? (
                            <FormFeedback type="invalid" className="d-block">
                              This time range overlaps another session.
                            </FormFeedback>
                          ) : null}
                        </Col>

                        <Col md={6}>
                          <Label className="form-label small">In location type</Label>
                          <LocationTypeSelect
                            value={s.inLocationType}
                            onChange={(v) => updateSession(idx, { inLocationType: v })}
                          />
                        </Col>

                        <Col md={6}>
                          <Label className="form-label small">Out location type</Label>
                          <LocationTypeSelect
                            value={s.outLocationType}
                            onChange={(v) => updateSession(idx, { outLocationType: v })}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                );
              })}

              {/* Add session */}
              <Col xs={12}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary w-100"
                  onClick={addSession}
                >
                  <i className="ri-add-line me-1" />
                  Add session
                </button>
              </Col>

              {/* Remarks */}
              <Col xs={12}>
                <Label className="form-label">Remarks</Label>
                <Input
                  type="textarea"
                  rows={2}
                  name="remarks"
                  value={validation.values.remarks || ""}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  placeholder="Optional note about this attendance entry"
                  invalid={!!(validation.touched.remarks && validation.errors.remarks)}
                />
                {validation.touched.remarks && validation.errors.remarks ? (
                  <FormFeedback type="invalid">
                    {validation.errors.remarks}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>
          </Form>
        </ModalBody>

        <ModalFooter>
          <div className="d-flex w-100 justify-content-between align-items-center gap-2">
            <div>
              {data ? (
                <Button
                  type="button"
                  color="danger"
                  outline
                  size="sm"
                  onClick={() => setDeleteModal(true)}
                >
                  <i className="ri-delete-bin-line me-1" />
                  Remove entry
                </Button>
              ) : null}
            </div>
            <div className="hstack gap-2">
              <Button type="button" color="light" onClick={handleOnClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="attendance-form"
                color="primary"
                disabled={validation.isSubmitting || hasOverlap}
              >
                {validation.isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    />
                    Saving…
                  </>
                ) : data ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default AttendanceForm;
